import {
    ClaimWorkflowConfiguration,
    ClaimWorkflowConfigurationEditPayload,
    CoreFetch,
    CreateClaimWorkflowConfigurationRequest,
    WorkerApi,
} from '../../api';
import { WorkerConfigurationApiMode, fetchClaimWorkflowConfiguration } from './worker-configuration.api';
import { canEditWorkflowOption } from './worker-configuration.permissions';
import {
    cloneWorkflowConfigMap,
    isUnsetConfigValue,
    parseWorkerWorkflowConfiguration,
    toNumericConfigString,
    WorkerWorkflowConfiguration,
    WorkflowConfigMap,
    WorkflowConfigValue,
} from './worker-configuration.types';

type ClaimWorkflowConfigurationRequestBody = Record<string, Record<string, unknown>>;

function isListConfigValue(value: WorkflowConfigValue | undefined): value is string[] {
    return Array.isArray(value);
}

function looksLikeListConfigKey(key: string): boolean {
    return /List$/i.test(key) || /Tags$/i.test(key);
}

function isNumericScalar(value: WorkflowConfigValue | undefined): value is number | string {
    return typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value));
}

function toPayloadValue(
    draftValue: WorkflowConfigValue | undefined,
    savedValue?: WorkflowConfigValue,
    key?: string,
): WorkflowConfigValue | null {
    if (isUnsetConfigValue(draftValue)) {
        // Cleared list options must persist as [] rather than null.
        if (isListConfigValue(savedValue) || (key != null && looksLikeListConfigKey(key))) {
            return [];
        }

        return null;
    }

    // Persist numeric scalars as digit-only strings for a stable payload type.
    if (typeof draftValue === 'number') {
        return toNumericConfigString(draftValue) ?? draftValue;
    }

    return draftValue;
}

function configValuesEqual(left: WorkflowConfigValue | undefined, right: WorkflowConfigValue | undefined): boolean {
    if (left === right) {
        return true;
    }

    if (isUnsetConfigValue(left) && isUnsetConfigValue(right)) {
        return true;
    }

    // 3 and "3" are the same numeric config value.
    if (isNumericScalar(left) && isNumericScalar(right)) {
        return toNumericConfigString(left) === toNumericConfigString(right);
    }

    return JSON.stringify(left) === JSON.stringify(right);
}

function collectWorkflowConfigChanges(
    draft: WorkflowConfigMap,
    saved: WorkflowConfigMap,
    apiMode: WorkerConfigurationApiMode = 'support',
): ClaimWorkflowConfigurationEditPayload[] {
    const changes: ClaimWorkflowConfigurationEditPayload[] = [];
    const statuses = new Set([...Object.keys(draft), ...Object.keys(saved)]);

    for (const workflowStatus of statuses) {
        const draftStatus = draft[workflowStatus] ?? {};
        const savedStatus = saved[workflowStatus] ?? {};
        const keys = new Set([...Object.keys(draftStatus), ...Object.keys(savedStatus)]);

        for (const key of keys) {
            if (!canEditWorkflowOption(apiMode, workflowStatus, key)) {
                continue;
            }

            const draftValue = draftStatus[key];
            const savedValue = savedStatus[key];

            if (!configValuesEqual(draftValue, savedValue)) {
                changes.push({
                    workflowStatus,
                    _configuration: key,
                    value: toPayloadValue(draftValue, savedValue, key) as unknown as ClaimWorkflowConfigurationEditPayload['value'],
                });
            }
        }
    }

    return changes;
}

function hasPersistedConfiguration(
    configuration: WorkerWorkflowConfiguration | null,
    savedConfig: WorkflowConfigMap,
): boolean {
    return Boolean(configuration?.createDate) || Object.keys(savedConfig).length > 0;
}

export { hasPersistedConfiguration };

function omitProviderReadonlyOptions(config: WorkflowConfigMap): WorkflowConfigMap {
    const next = cloneWorkflowConfigMap(config);

    for (const [status, statusConfig] of Object.entries(next)) {
        for (const key of Object.keys(statusConfig)) {
            if (!canEditWorkflowOption('provider', status, key)) {
                delete statusConfig[key];
            }
        }

        if (Object.keys(statusConfig).length === 0) {
            delete next[status];
        }
    }

    return next;
}

export async function saveClaimWorkflowConfiguration(
    draftConfig: WorkflowConfigMap,
    savedConfig: WorkflowConfigMap,
    configuration: WorkerWorkflowConfiguration | null,
    apiMode: WorkerConfigurationApiMode = 'support',
): Promise<WorkerWorkflowConfiguration> {
    const api = CoreFetch<WorkerApi>(WorkerApi);

    if (!hasPersistedConfiguration(configuration, savedConfig)) {
        const requestBody: ClaimWorkflowConfigurationRequestBody =
            apiMode === 'provider' ? omitProviderReadonlyOptions(draftConfig) : draftConfig;
        const created =
            apiMode === 'provider'
                ? await api.createClaimWorkflowConfigurationProvider({
                      requestBody: requestBody as CreateClaimWorkflowConfigurationRequest['requestBody'],
                  })
                : await api.createClaimWorkflowConfiguration({
                      requestBody: requestBody as CreateClaimWorkflowConfigurationRequest['requestBody'],
                  });
        return parseWorkerWorkflowConfiguration(created);
    }

    const changes = collectWorkflowConfigChanges(draftConfig, savedConfig, apiMode);
    if (changes.length === 0) {
        const latest = await fetchClaimWorkflowConfiguration(apiMode);
        return parseWorkerWorkflowConfiguration(latest);
    }

    let latest: ClaimWorkflowConfiguration | undefined;
    for (const change of changes) {
        latest =
            apiMode === 'provider'
                ? await api.updateClaimWorkflowConfigurationProvider({
                      claimWorkflowConfigurationEditPayload: change,
                  })
                : await api.updateClaimWorkflowConfiguration({
                      claimWorkflowConfigurationEditPayload: change,
                  });
    }

    return parseWorkerWorkflowConfiguration(latest ?? (await fetchClaimWorkflowConfiguration(apiMode)));
}

export function createEmptyWorkflowConfigMap(): WorkflowConfigMap {
    return cloneWorkflowConfigMap();
}
